import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Message,
  MessageAuthor,
  MessageType,
  PushMessage,
  UpdateMessage,
} from "../types";

export type ChatMessageState = {
  messages: Message[];
  pushMessage: (message: PushMessage) => string;
  updateMessage: (message: UpdateMessage) => void;
  latestBotMessageId: string;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  reset: () => void;
};

export const defaultChatMessageState: ChatMessageState = {
  messages: [],
  pushMessage: () => "",
  updateMessage: () => null,
  latestBotMessageId: "",
  sessionId: "",
  setSessionId: () => null,
  reset: () => null,
};

export const ChatMessageState = createContext<ChatMessageState>(
  defaultChatMessageState
);

export const initialMessagesTexts = [
  "Hello! A few legal bits before we get started. In the course of communicating with me, you may receive product suggestions. These suggestions do not take into account all of your individual circumstances. These suggestions do not follow a holistic review, such as one provided by an independent financial adviser. The products suggested will only be Lloyds products. You will not be charged during the course of this conversation, or for any suggestions you receive. If you choose to sign up for a product suggested in this chat, Lloyds may receive payment for those products. Before you decide to sign up for a suggested product, you should consider alternative products from other institutions.",
  "I'm here to help you find financial products that might suit your situation. To do that, I'll ask you a few questions about your finances and what you're hoping to achieve. Let's start simple - is there something specific you're looking to do with your money right now, like saving for something, managing debt, or growing your wealth?",
];

export const ChatStateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [latestBotMessageId, setLatestBotMessageId] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("");

  const hasInitialised = useRef(false);

  const pushMessage = useCallback(
    (message: PushMessage): string => {
      const id = uuidv4();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          ...message,
          id,
          type: MessageType.Text,
          sentAt: new Date().toISOString(),
        },
      ]);
      if (message.author === MessageAuthor.Bot) {
        setLatestBotMessageId(id);
      }
      return id;
    },
    [setMessages, setLatestBotMessageId]
  );

  const updateMessage = useCallback(
    ({ id, text, widget, question }: UpdateMessage) => {
      setMessages((prevMessages) => {
        const indexToUpdate = prevMessages.findIndex(
          (message) => message.id === id
        );
        if (indexToUpdate === -1) return prevMessages;
        return [
          ...prevMessages.slice(0, indexToUpdate),
          {
            ...prevMessages[indexToUpdate],
            ...(text !== undefined && { text }),
            ...(widget !== undefined && { widget }),
            ...(question !== undefined && { question }),
          },
          ...prevMessages.slice(indexToUpdate + 1),
        ];
      });
    },
    [setMessages]
  );

  const sendInitalBotMessages = useCallback(() => {
    // Push initial bot messages with loading state, one at a time
    initialMessagesTexts.forEach((text, index) => {
      const delay = index * 500; // Stagger message appearance

      setTimeout(() => {
        const loadingMessageId = pushMessage({
          author: MessageAuthor.Bot,
          text: "",
        });

        setTimeout(() => {
          updateMessage({
            id: loadingMessageId,
            text: text,
          });
        }, 500); // Simulate loading state for 500ms
      }, delay);
    });
  }, [pushMessage, updateMessage]);

  const reset = useCallback(() => {
    setMessages([]);
    setLatestBotMessageId("");
    setSessionId("");
    // Reset the bot message
    sendInitalBotMessages();
  }, [pushMessage, updateMessage]);

  useEffect(() => {
    if (hasInitialised.current || messages.length > 0) {
      return;
    }
    hasInitialised.current = true;

    sendInitalBotMessages();
  }, []);

  return (
    <ChatMessageState.Provider
      value={{
        messages,
        pushMessage,
        updateMessage,
        latestBotMessageId,
        reset,
        sessionId,
        setSessionId,
      }}
    >
      {children}
    </ChatMessageState.Provider>
  );
};

export const useChatMessageState = (): ChatMessageState => {
  const context = useContext(ChatMessageState);
  if (!context) {
    throw new Error(
      "useChatMessageState must be used within a ChatMessageStateProvider"
    );
  }
  return context;
};
